import { Action } from 'redux';
import _ from 'underscore';

import { isUpdateContentDetailAction } from '../../app/actions/spaces/GetContentDetailAction';
import { isRecommendationHiddenAction, isRecommendationUnhiddenAction } from '../../recommendations/actions/HideRecommendationAction';
import { isDraftsInfoAvailable } from '../../drafts/actions/DraftsActions';
import { DraftContent } from '../../drafts/types/DraftsTypes';
import { ExplorableItemType, isRecommendationType } from '../../ts/models/ContentType';
import { IContentInfo } from '../../ts/models/IContentInfo';
import { ISharedWithMeContentInfo, removeSharedWithMeSpecificData } from '../../ts/models/ISharedWithMeContentInfo';
import { IViewInfo } from '../../ts/models/IViewInfo';
import { ContentDraftStatus } from '../../ts/ServerApi/types/ContentDraft';
import {
  isHomeAddFavoriteSucceededAction,
  ishomeFetchFavoritesSucceededAction,
  ishomeFetchPopularSucceededAction,
  ishomeFetchRecentsSucceededAction,
  ishomeFetchRecommendationsSucceededAction,
  ishomeFetchSharedWithMeSucceededAction,
  isHomeRemoveFavoriteSucceededAction
} from '../actions/HomeActions';
import { NormalizedContent } from '../types/HomeTypes';

export const initialState: NormalizedContent = {};

// Optional prefix for normalizedContentKey to allow different data model for the same content type and content id 
export enum ContentKeyPrefix {
  sharedWithMe = 'sharedWithMe',
}

export function normalizedContentKey(contentId: string, contentType: ExplorableItemType, prefix?: ContentKeyPrefix) {
  return prefix ? `${prefix}-${contentType}:${contentId}` : `${contentType}:${contentId}`;
}

function findKeyOfContentByLuid(content: NormalizedContent, luid: string): string {
  return _.findKey(content, c => c?.luid === luid);
}

function getUpdatedContentInfo(previousContent: IContentInfo, draft: DraftContent): IContentInfo {
  return {...previousContent,
          draftLuid: draft.draftLuid,
          draftStatus: draft.parentContentLuid ? ContentDraftStatus.unpublishedchanges : ContentDraftStatus.unpublished
  };
}

export function normalizedContentReducer(previousState: NormalizedContent = initialState, action: Action): NormalizedContent {
  if(ishomeFetchFavoritesSucceededAction(action) ||
     ishomeFetchRecentsSucceededAction(action) ||
     ishomeFetchRecommendationsSucceededAction(action) ||
     ishomeFetchPopularSucceededAction(action)) {
    const newState: NormalizedContent = {...previousState};
    action.result.items.forEach((item: IContentInfo) => {
      newState[normalizedContentKey(item.id, item.contentType)] = {...item};

      // Duplicate recommended views (contentType: 'view'), to support favorite actions in channel
      // home page stores all views in an unified array, recents / favorites retrieve views by id
      if (isRecommendationType(item.contentType)) {
        const recommendedView_ViewTypeCast = {...item, contentType: 'view' as ExplorableItemType};
        newState[normalizedContentKey(item.id, recommendedView_ViewTypeCast.contentType)] = {...recommendedView_ViewTypeCast};
      }
    });
    return newState;
  }

  if (ishomeFetchSharedWithMeSucceededAction(action)) {
    // FetchSharedWithMeSucceededAction requires special treatment because data that's specific to 
    // Shared with Me (e.g, sharer info and share date) should be isolated to only show up in
    // Shared with Me channel, but data that's not specific to Shared with Me (e.g. content id,
    // content type, and content name) need to be stored in home state as well to support rendering 
    // a card in favorite channel after clicking favorite button (TFSID 1125832)
    const newState: NormalizedContent = {...previousState};
    action.result.items.forEach((item: IContentInfo) => {
      newState[normalizedContentKey(item.id, item.contentType, ContentKeyPrefix.sharedWithMe)] = {...item};
      newState[normalizedContentKey(item.id, item.contentType)] = removeSharedWithMeSpecificData(item as ISharedWithMeContentInfo);
    });
    return newState;
  }

  if (isUpdateContentDetailAction(action)) {
    const newState: NormalizedContent = {...previousState};
    action.content.forEach(card => {
      const key: string = normalizedContentKey(card.id, card.contentType);
      newState[key] = {...newState[key], ...card};
    });
    return newState;
  }

  if(isHomeAddFavoriteSucceededAction(action) || isHomeRemoveFavoriteSucceededAction(action)) {
    const key: string = normalizedContentKey(action.contentId, action.contentType);
    const newState: NormalizedContent = {...previousState};
    newState[key] = {...newState[key], favorite: isHomeAddFavoriteSucceededAction(action)};

    // Check if sharedWithMe content requires the favorite state update
    const sharedWithMeKey: string = normalizedContentKey(action.contentId, action.contentType, ContentKeyPrefix.sharedWithMe);
    if (newState[sharedWithMeKey]) {
      newState[sharedWithMeKey] = {...newState[sharedWithMeKey], favorite: isHomeAddFavoriteSucceededAction(action)};
    }

    // Check if recommendedView requires the favorite state update
    // This check helps keep recommendedView favorite state updated
    const recommendedViewKey: string = normalizedContentKey(action.contentId, 'recommendedView');
    if (newState[recommendedViewKey]) {
      if (isHomeAddFavoriteSucceededAction(action) && !newState[recommendedViewKey].favorite) {
        (newState[recommendedViewKey] as IViewInfo).usageInfo.favoritesTotal += 1;
      } else if (isHomeRemoveFavoriteSucceededAction(action) &&
                 newState[recommendedViewKey].favorite) { // unfav undo toast can be stacked, check status of favorite to ensure the count only changes once
        (newState[recommendedViewKey] as IViewInfo).usageInfo.favoritesTotal -= 1;
      }
      newState[recommendedViewKey] = {...newState[recommendedViewKey], favorite: isHomeAddFavoriteSucceededAction(action)};
    }

    return newState;
  }

  if(isRecommendationHiddenAction(action) || isRecommendationUnhiddenAction(action)) {
    const key: string = normalizedContentKey(action.contentId, action.contentType);
    const newState: NormalizedContent = {...previousState};
    newState[key] = {...newState[key], hide: isRecommendationHiddenAction(action)};

    return newState;
  }

  //TODO: this only adds draft to existing content no unpublished drafts
  if (isDraftsInfoAvailable(action)) {
    const newState: NormalizedContent = {...previousState};
    let returnNewState = false;
    action.contents.forEach(draft => {
      const contentKey = findKeyOfContentByLuid(newState, draft.parentContentLuid);
      if (contentKey) {
        returnNewState = true;
        newState[contentKey] = getUpdatedContentInfo(newState[contentKey], draft);
      }
    });
    return returnNewState ? newState : previousState;
  }
  return previousState;
}
